import React, { useCallback, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Button,
  Chip,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Collapse
} from '@mui/material'
import {
  DragIndicator,
  Search,
  CheckBox,
  CheckBoxOutlineBlank,
  RestartAlt,
  Store,
  Inventory2,
  ShoppingCart,
  LocalShipping,
  DynamicFeed,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useFieldSelection } from '../hooks/useFieldSelection'

export interface ExportField {
  id: string
  name: string
  api_field: string
  export_heading: string
  type: 'text' | 'number' | 'currency' | 'datetime' | 'boolean' | 'array'
  default_enabled: boolean
  description: string
  requires_inventory?: boolean
  requires_line_items?: boolean
  requires_outlets?: boolean
  requires_sales?: boolean
  requires_consignments?: boolean
  field_type?: 'static' | 'dynamic'
  column_generator?: string
}

export interface FieldConfiguration {
  export_type: string
  title: string
  fields: ExportField[]
}

export interface SelectedField extends ExportField {
  enabled: boolean
  order: number
}

interface FieldSelectorProps {
  configuration: FieldConfiguration | null
  onFieldsChange: (selectedFields: SelectedField[]) => void
  exportType?: string
}

interface SortableFieldItemProps {
  field: SelectedField
  onToggle: (fieldId: string) => void
}

function SortableFieldItem({ 
  field, 
  onToggle
}: SortableFieldItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const getFieldTypeColor = (type: string) => {
    switch (type) {
      case 'currency': return 'success'
      case 'datetime': return 'warning'  
      case 'number': return 'info'
      case 'boolean': return 'secondary'
      default: return 'default'
    }
  }

  const isDisabled = false  // All fields are now selectable

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      sx={{
        px: 1,
        py: 0.25,
        mb: 1,
        border: field.enabled ? 1.5 : 0.5,
        borderColor: field.enabled ? 'primary.main' : 'divider',
        opacity: isDisabled ? 0.6 : 1,
        cursor: isDragging ? 'grabbing' : 'default'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          {...attributes}
          {...listeners}
          size="small"
          sx={{ 
            cursor: 'grab', 
            '&:active': { cursor: 'grabbing' },
            p: 0.25,
            minWidth: 'unset',
            '& .MuiSvgIcon-root': { fontSize: '1rem' }
          }}
        >
          <DragIndicator />
        </IconButton>

        <FormControlLabel
          control={
            <Checkbox
              checked={field.enabled}
              onChange={() => onToggle(field.id)}
              disabled={isDisabled}
              size="small"
              sx={{ 
                color: 'primary.main',
                '&.Mui-checked': { color: 'primary.main' },
                p: 0.25,
                '& .MuiSvgIcon-root': { fontSize: '1.1rem' }
              }}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" fontWeight={field.enabled ? 600 : 400}>
                {field.name}
              </Typography>
              <Chip
                label={field.type}
                size="small"
                color={getFieldTypeColor(field.type) as any}
                variant="outlined"
                sx={{ 
                  height: '20px', 
                  fontSize: '0.7rem',
                  '& .MuiChip-label': { px: 0.5, py: 0 }
                }}
              />
              {field.field_type === 'dynamic' && (
                <Tooltip title="This field will add multiple columns to your export">
                  <DynamicFeed sx={{ fontSize: '1rem' }} color="warning" />
                </Tooltip>
              )}
              {[
                { active: field.requires_consignments, Icon: LocalShipping, label: 'Requires stock-order (consignment) data' },
                { active: field.requires_inventory, Icon: Inventory2, label: 'Requires inventory data' },
                { active: field.requires_sales, Icon: ShoppingCart, label: 'Requires sales data' },
                { active: field.requires_outlets, Icon: Store, label: 'Requires outlet data' }
              ]
                .filter(req => req.active)
                .map(({ Icon, label }) => (
                  <Tooltip key={label} title={`${label} (auto-fetched when selected)`}>
                    <Icon sx={{ fontSize: '1rem' }} color="success" />
                  </Tooltip>
                ))}
            </Box>
          }
          sx={{ flexGrow: 1, m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
        />
      </Box>
    </Paper>
  )
}

function FieldSelector({ 
  configuration, 
  onFieldsChange,
  exportType
}: FieldSelectorProps) {
  const [collapsed, setCollapsed] = useState(true)
  
  const {
    fields,
    filteredFields,
    searchTerm,
    setSearchTerm,
    toggleField,
    selectAll,
    deselectAll,
    reorderFields,
    resetToDefaults,
    hasStoredSelections,
    enabledCount,
    availableCount
  } = useFieldSelection({
    configuration,
    exportType
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Notify parent when enabled fields change
  const notifyFieldsChange = useCallback(() => {
    const enabledFields = fields
      .filter(field => field.enabled)
      .sort((a, b) => a.order - b.order)
    onFieldsChange(enabledFields)
  }, [fields, onFieldsChange])

  // Use useEffect to notify parent of changes
  React.useEffect(() => {
    notifyFieldsChange()
  }, [notifyFieldsChange])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex(field => field.id === active.id)
      const newIndex = fields.findIndex(field => field.id === over.id)
      reorderFields(oldIndex, newIndex)
    }
  }

  // All fields are now selectable - requirements are auto-detected

  if (!configuration) {
    return (
      <Alert severity="info">
        Loading field configuration...
      </Alert>
    )
  }

  return (
    <Box>
      {/* Clickable Header */}
      <Box 
        onClick={() => setCollapsed(!collapsed)}
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
          p: 1,
          borderRadius: 1,
          mb: 1
        }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          📋 Export Fields ({enabledCount}/{availableCount} selected)
        </Typography>
        <IconButton size="small">
          {collapsed ? <ExpandMore /> : <ExpandLess />}
        </IconButton>
      </Box>

      <Collapse in={!collapsed}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose and reorder the fields to include in your export. Required data (inventory, outlets) is automatically fetched based on your selection. Drag fields to change their order in the CSV.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search fields..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ minWidth: 200 }}
          />
          
          <Button
            variant="outlined"
            size="small"
            startIcon={<CheckBox />}
            onClick={selectAll}
          >
            All
          </Button>
          
          <Button
            variant="outlined"
            size="small"
            startIcon={<CheckBoxOutlineBlank />}
            onClick={deselectAll}
          >
            None
          </Button>

          {hasStoredSelections && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<RestartAlt />}
              onClick={resetToDefaults}
              sx={{ ml: 1 }}
            >
              Reset
            </Button>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {filteredFields.length === 0 && searchTerm && (
          <Alert severity="info">
            No fields match your search term "{searchTerm}"
          </Alert>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={filteredFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {filteredFields.map((field) => (
                <SortableFieldItem
                  key={field.id}
                  field={field}
                  onToggle={toggleField}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>

        {enabledCount === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Please select at least one field to export.
          </Alert>
        )}
      </Collapse>
    </Box>
  )
}

export default FieldSelector